import {
  Datagrid,
  DateField,
  DeleteWithConfirmButton,
  EditButton,
  FunctionField,
  TextField,
  WrapperField,
  TopToolbar,
  ReferenceInput,
  TextInput,
  SelectInput,
  DateInput,
  List,
  useTranslate,
  useLocaleState,
  useListContext,
  useGetList,
  usePermissions,
} from "react-admin";
import { useMediaQuery, Theme, Button } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import { ImagingStudyList } from "../../imagingstudies/components/ImagingStudyList";
import BulkActionButtons, { dlExportCSV } from "shared/components/BulkActionButtons";
import LoadingButton from "shared/components/LoadingButton";
import AnalysisStatusField from "shared/components/AnalysisStatusField";
import DurationField from "shared/components/DurationField";

const ListActions = () => {
  const { filterValues, setFilters, sort, setSort, } = useListContext();
  return (
    <TopToolbar style={{ marginBottom: 0 }}>
      {Object.keys(filterValues)?.length || sort ? (
        <Button
          style={{ lineHeight: 1.5 }}
          size="small"
          onClick={() => {
            setFilters({}, {}, false)
            setSort({ field: 'id', order: 'DESC' })
          }}
        >
          {" "}
          Reset{" "}
        </Button>
      ) : null}
      <LoadingButton variant='contained'
        promise={() => dlExportCSV({ filterValues, sort })}
      >
        Export CSV
      </LoadingButton>
    </TopToolbar>
  );
};

const patientListFilters = [
  <TextInput
    className="w-48"
    source="identifier"
    label="resources.patients.filter.identifier.match"
    alwaysOn
  />,
  <TextInput
    source="name"
    label="resources.patients.filter.name.match"
    alwaysOn
  />,
  <DateInput source="birthdate" alwaysOn />,
  <SelectInput
    source="gender"
    choices={[
      { id: "male", name: "resources.patients.filter.gender.male" },
      { id: "female", name: "resources.patients.filter.gender.female" },
    ]}
    emptyText="resources.patients.filter.gender.empty"
    emptyValue={0}
    alwaysOn
  />,
  <ReferenceInput
    source="group"
    reference="analyzis_groups"
    label="resources.patients.filter.groups"
    alwaysOn

  >
    <SelectInput
      label="resources.patients.filter.groups"
      emptyText="resources.patients.filter.groups.empty"
      emptyValue={undefined}
      optionText="name"
    />
  </ReferenceInput>,
  <DateInput
    source="examdatestart"
    label="resources.analyses.filter.acquisition.start"
    alwaysOn
  />,
  <DateInput
    source="examdateend"
    label="resources.analyses.filter.acquisition.end"
    alwaysOn
  />,
];

export const PatientList = () => {
  const isLarge = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const t = useTranslate();
  const { permissions } = usePermissions();
  const [locale] = useLocaleState();
  const { data: groups } = useGetList("analyzis_groups");

  return (
    <List
      actions={<ListActions />}
      filters={patientListFilters.filter(
        (p) => p.props["source"] !== "groups" || groups?.length >= 2
      )}
      sort={{ field: "id", order: "DESC" }}
    >
      <Datagrid
        expand={<ImagingStudyList />}
        bulkActionButtons={<BulkActionButtons />}
        rowClick="expand"
      >
        <TextField
          source="identifier"
          label={t("resources.patients.fields.id")}
        />
        <TextField
          source="name"
          label={t("resources.patients.fields.name")}
        />
        {isLarge ? (
          <DateField
            source="birth_date"
            locales={locale}
            label={t("resources.patients.fields.birthdate")}
            options={{ year: "numeric", month: "2-digit", day: "2-digit" }}
          />
        ) : null}
        {isLarge ? (
          <FunctionField
            source="gender"
            label={t("resources.patients.fields.gender")}
            render={({ gender }) =>
              gender ? t("resources.patients.filter.gender." + gender) : ""
            }
          />
        ) : null}
        {isLarge && groups?.length >= 2 ? (
          <TextField
            sortable={false}
            source="group"
            label={t("resources.analyses.fields.preset")}
          />
        ) : null}
        {isLarge ? (
          <DurationField
            sortable={false}
            data-testid={"duration-field"}
            source="analysis_duration"
            label={t("resources.analyses.fields.duration")}
          />
        ) : null}
        <AnalysisStatusField
          data-testid={`analysis-status-field`}
          sortable={false}
          source="analysis_status"
          label={t("resources.analyses.fields.status")}
        />
        <FunctionField
          label={t("resources.patients.fields.imagingStudyCount")}
          render={(record) => record.studies?.length
          }
        />
        <WrapperField cellClassName="text-right">
          {permissions.role.name === "SuperAdmin" ? <EditButton /> : null}
          <DeleteWithConfirmButton
            className="!text-right"
            label={null}
            confirmContent="ra.confirm.delete"
            icon={<DeleteIcon />}
          />
        </WrapperField>
      </Datagrid>
    </List>
  );
};
